
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import DataPortabilityForm from './DataPortabilityForm';
import DataDeletionForm from './DataDeletionForm';
import ConsentManagement from './ConsentManagement';
import PrivacyPolicyViewer from './PrivacyPolicyViewer';

const PrivacyControls = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const { toast } = useToast();

  const handleDataExport = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // This would trigger a backend function to prepare user data export
      toast({
        title: "Data export requested",
        description: "We'll prepare your data export and email you a download link within 30 days.",
      });
    } catch (error) {
      console.error('Error requesting data export:', error);
      toast({
        title: "Error",
        description: "Failed to request data export. Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'consent':
        return <ConsentManagement />;
      case 'portability':
        return <DataPortabilityForm />;
      case 'deletion':
        return <DataDeletionForm />;
      case 'policy':
        return <PrivacyPolicyViewer />;
      default:
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Privacy Rights</CardTitle>
                <CardDescription>
                  Manage your data and privacy preferences in accordance with GDPR and CCPA regulations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setActiveSection('consent')}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Consent Management</CardTitle>
                      <CardDescription>
                        Review and update your data processing consents
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  
                  <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setActiveSection('portability')}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Data Portability</CardTitle>
                      <CardDescription>
                        Download a copy of your personal data
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  
                  <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setActiveSection('deletion')}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Data Deletion</CardTitle>
                      <CardDescription>
                        Request deletion of your personal data
                      </CardDescription>
                    </CardHeader>
                  </Card>
                  
                  <Card className="cursor-pointer hover:bg-gray-50" onClick={() => setActiveSection('policy')}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Privacy Policy</CardTitle>
                      <CardDescription>
                        Read our complete privacy policy
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {activeSection !== 'overview' && (
        <Button 
          variant="ghost" 
          onClick={() => setActiveSection('overview')}
          className="mb-4"
        >
          ← Back to Privacy Overview
        </Button>
      )}
      
      {renderContent()}
    </div>
  );
};

export default PrivacyControls;
